import React from "react";

import { css, cx } from "@emotion/css";
import { useTranslation } from "react-i18next";

import { FontSizes } from "@/assets/fonts.ts";
import { Icons } from "@/assets/icons.ts";
import type { UseRichTextState } from "@/hooks/useRichTextState.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { Button } from "@/uiKit/atoms/Button.tsx";
import type { StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { RichTextEditor } from "@/uiKit/molecules/RichText/RichTextEditor.tsx";
import { RichTextViewer } from "@/uiKit/molecules/RichText/RichTextViewer.tsx";

export type RichTextProps = {
  richTextState: UseRichTextState;
  placeholder?: string;
  createNewTextTitle: string;
  editTextTitle: string;
  disabledEdition?: boolean;
  hideActions?: boolean;
  hideHeader?: boolean;
  /** Prefix of the ids, for pages rendering several rich texts at once: the root is `${idPrefix}-rich-text` (default `rich-text`), see `RichTextEditor` for the editor. */
  idPrefix?: string;
  className?: string;
  classes?: StyleClasses<"root" | "editor" | "editorContent" | "viewer" | "actions">;
};

export const RichText: React.FC<RichTextProps> = ({
  richTextState,
  createNewTextTitle,
  editTextTitle,
  placeholder,
  hideHeader = false,
  disabledEdition = false,
  hideActions = false,
  idPrefix,
  className,
  classes,
}) => {
  const { t } = useTranslation();
  const styles = useStyles(makeStyles);

  return (
    <div
      id={idPrefix ? `${idPrefix}-rich-text` : "rich-text"}
      className={cx(styles.container, className, classes?.root)}
    >
      {richTextState.editing && (
        <RichTextEditor
          hideHeader={hideHeader}
          idPrefix={idPrefix}
          content={richTextState.content}
          onChange={richTextState.editText}
          classes={{ root: cx(styles.editor, classes?.editor), content: classes?.editorContent }}
          placeholder={placeholder}
        />
      )}
      {!richTextState.editing && !richTextState.isNewText && (
        <RichTextViewer className={cx(styles.viewer, classes?.viewer)} content={richTextState.content} />
      )}
      {!richTextState.editing && richTextState.isNewText && !disabledEdition && (
        <Button
          data-action="rich-text.create"
          variant="text"
          className={styles.button}
          startIcon={<Icons.plus className={styles.icon} />}
          onClick={richTextState.edit}
          id="create-new-rich-text"
        >
          {createNewTextTitle}
        </Button>
      )}
      {!hideActions && (
        <div className={cx(styles.actions, classes?.actions)}>
          {!richTextState.editing && !richTextState.isNewText && !disabledEdition && (
            <Button
              data-action="rich-text.edit"
              variant="text"
              startIcon={<Icons.edit className={styles.icon} />}
              onClick={richTextState.edit}
              className={styles.button}
              id="edit-rich-text"
            >
              {editTextTitle}
            </Button>
          )}
          {richTextState.editing && (
            <Button
              data-action="rich-text.cancel"
              variant="contained"
              onClick={richTextState.cancel}
              id="cancel-rich-text-edition"
            >
              {t("button.cancel")}
            </Button>
          )}
          {richTextState.editing && (
            <Button data-action="rich-text.save" variant="contained" onClick={richTextState.save} id="save-rich-text">
              {t("button.save")}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

const makeStyles = (theme: Theme) => ({
  container: css({
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    flex: "1 1 auto",
    overflow: "clip",
    maxWidth: "100%",
    gap: theme.spacing(4),
    minHeight: 0,
  }),
  actions: css({
    display: "flex",
    gap: theme.spacing(4),
  }),
  editor: css({
    display: "flex",
    flexDirection: "column",
    minHeight: theme.spacing(48),
    ".ProseMirror": {
      minHeight: theme.spacing(48),
    },
    overflow: "auto",
  }),
  viewer: css({
    display: "flex",
    minHeight: 0,
    flexShrink: 1,
    overflow: "auto",
  }),
  button: css({
    minWidth: 0,
    width: "fit-content",
  }),
  icon: css({
    width: FontSizes.body,
    height: FontSizes.body,
  }),
});
